import Consent from "../../components/consent/Consent";

import { missingOrInvalidField } from "../../utils/Enquiries";

const toSnakeCase = string => string.replace(/([A-Z]+)/g, "_$1").replace(/^_/, "");

const VIEWS = {
  PERSONAL_DETAILS: ["Enquiry, Personal Details", "/enquiries/personal_details"],
  TRAVEL_DETAILS: ["Enquiry, Travel Details", "/enquiries/travel_details"],
  ENQUIRY_COMPLETE: ["Enquiry, Completed", "/enquiries/completed"],
  BOOKING_CUSTOMIZE_OFFER: ["Customize Offer", "/booking/customize-offer"],
  BOOKING_PERSONAL_DETAILS: ["Personal Details", "/booking/personal-details"],
  BOOKING_STRIPE_CHECKOUT: ["Stripe Checkout", "/booking/checkout"],
  BOOKING_COMPLETE: ["Booking Complete", "/booking/complete"]
};

const CONSENT_COOKIES = {
  perm: "permConsent",
  sess: "sessConsent"
};

const GOALS = {
  CLICKED_PACKAGE: ["Search Page", "Clicked Package"],
  CLICKED_DYNAMIC_PACKAGE: ["Dynamic Page", "Clicked Package"],
  VIEWED_DYNAMIC: ["Dynamic Page View", "Success"],
  VIEWED_REGION: ["Region View", "Success"],
  VIEWED_VENUE: ["Venue View", "Success"]
};

const COMMON_METRICS = ["goal", "ecommerce", "remarketing"];

const consent = new Consent(CONSENT_COOKIES.perm, CONSENT_COOKIES.sess);

let lastVisibilityChange;

const pushDataLayer = payload => {
  if (!payload) {
    return;
  }

  window.dataLayer.push(payload);
};

const pushGAEvent = (type, payload) => {
  if (!payload.eventCategory || !payload.eventAction) {
    return;
  }

  pushDataLayer({ event: type, ...payload });
};

const trackCommonMetrics = (data) => {
  const commonMetrics = consent.get().consented
    ? COMMON_METRICS
    : COMMON_METRICS.filter(metric => metric !== "remarketing");

  commonMetrics.forEach(metric => pushDataLayer(data[metric]));
};

const gaEvent = (eventCategory, eventAction, eventLabel) => {
  pushGAEvent("GAEvent", {
    eventCategory,
    eventAction,
    eventLabel
  });
};

const gaEventNoHit = (eventCategory, eventAction, eventLabel) => {
  pushGAEvent("GAEventNoHit", {
    eventCategory,
    eventAction,
    eventLabel
  });
};

const dynamicRemarketing = (remarketingData) => {
  if (consent.get().consented) {
    pushDataLayer({ event: "dynamicRemarketing", remarketingData });
  }
};

const virtualPageView = pageView => {
  const view = VIEWS[pageView] || VIEWS[toSnakeCase(pageView).toUpperCase()];

  if (!view) {
    throw new ReferenceError(`virtualPageView: "${pageView}" not found`);
  }

  const [virtualPageTitle, virtualPageURL] = view;

  pushDataLayer({ event: "VirtualPageview", virtualPageTitle, virtualPageURL });
};

const trackGoal = goalName => {
  const goal = GOALS[goalName];

  if (!goal) {
    return;
  }
  const [action, label] = goal;

  gaEvent("Goals", action, label);
};

const trackEnquiry = data => {
  trackCommonMetrics(data);
  virtualPageView("ENQUIRY_COMPLETE");
};

const trackLastMinuteDeal = data => {
  trackCommonMetrics(data);
  virtualPageView("BOOKING_COMPLETE");
};

const trackClick = target => {
  const { gtmCategory, gtmAction, gtmLabel, gtmPageview } = target.dataset;

  if (gtmCategory) {
    gaEvent(gtmCategory, gtmAction, gtmLabel || target.href);
  }

  if (gtmPageview) {
    virtualPageView(gtmPageview);
  }
};

const trackInvalidEnquiryForm = target => {
  const formType = target.form.dataset.gtmFormType || "Enquiry Form";

  gaEventNoHit("Failed Enquiry", formType, missingOrInvalidField(target));
};

const trackServerSideEnquiryError = target => {
  const trackedError = "Failed enquiry: 422 error";
  const formType = target.dataset.gtmFormType || "Enquiry Form";

  gaEventNoHit("Failed Enquiry", formType, trackedError);
};

const trackVisibilityState = () => {
  const now = new Date().getTime();
  const { dataLayer } = window;
  let state = document.visibilityState;

  if (!lastVisibilityChange) {
    const GTM_START = "gtm.start";
    const start = dataLayer.find(entry => entry && entry[GTM_START]);

    lastVisibilityChange = start ? start[GTM_START] : now - 1;
  } else {
    state = state === "hidden" ? "visible" : "hidden";
  }

  const diff = now - lastVisibilityChange;

  if (diff) {
    pushGAEvent("GAEventNoHit", {
      eventCategory: "Page Visibility",
      eventAction: state,
      eventLabel: location.pathname,
      eventValue: diff
    });
  }

  lastVisibilityChange = now;
};

const trackTotalVisibility = (visible) => {
  const eventAction = visible ? "visible" : "hidden";
  const eventCategory = `Total Page ${visible ? "Visibility" : "Invisibility"}`;
  const total = window.dataLayer
    .filter(Boolean)
    .filter(entry => entry.eventCategory === "Page Visibility" && entry.eventAction === eventAction)
    .reduce((acc, entry) => acc + entry.eventValue, 0);

  pushGAEvent("GAEventNoHit", {
    eventCategory,
    eventAction: "Page Unload",
    eventLabel: location.pathname,
    eventValue: Math.min(total, 30 * 60 * 1e3)
  });
};

window.dataLayer = window.dataLayer || [];

window.addEventListener("load", trackVisibilityState);
window.addEventListener("beforeunload", () => {
  trackVisibilityState();
  trackTotalVisibility(true);
  trackTotalVisibility();
});

document.addEventListener("visibilitychange", trackVisibilityState);

export {
  dynamicRemarketing,
  gaEvent,
  gaEventNoHit,
  pushDataLayer,
  trackClick,
  trackEnquiry,
  trackGoal,
  trackInvalidEnquiryForm,
  trackLastMinuteDeal,
  trackServerSideEnquiryError,
  virtualPageView,
  VIEWS
};



// WEBPACK FOOTER //
// ./shared/helpers/gtm_tracking.js